import React, { useCallback, useMemo, useState, memo } from "react";
import { Button, Stack } from "react-bootstrap";
import { Flex, Segmented } from "antd";
import { useForm, Controller as RHFController } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { Col, Container, Row, Spinner } from "reactstrap";
import { HugeiconsIcon } from "@hugeicons/react";
import { Search01FreeIcons } from "@hugeicons/core-free-icons";
import { toast } from "react-toastify";
import { PageHeader } from "@/CommonElements/PageTitle";
import { AntInput } from "@/CommonElements/AntInput";
import { LabelValue } from "@/Component/Value";
import { useLocalizedText } from "@/utils/hooks/useLocalizedText";
import { useNusukCardsLookupPilgrimsMutation } from "@/services/nusukCards/nusukCards";
import { useCachedBasicData } from "@/utils/hooks/useCachedBasicData";
import { NUSUK_CITY_ID } from "@/utils/constants";
import { NusukCardsInquiryTable } from "./components/NusukCardsInquiryTable";

const LOOKUP_TYPES = { campaign: "campaign", group: "group" };

const formatContracts = (contracts) =>
  contracts.length > 0
    ? contracts
        .map((c) =>
          c.house_commercial_name_ar
            ? `${c.id} - ${c.house_commercial_name_ar}`
            : String(c.id)
        )
        .join(" , ")
    : "-";

const EntityInfoCard = memo(function EntityInfoCard({
  entity,
  stats,
  makkahContracts,
  madinahContracts,
  t,
  getLocalizedText,
  totalPilgrims,
}) {
  if (!entity) return null;

  return (
    <div className="card border rounded-3 mb-0 mt-3">
      <div className="card-body py-3 px-4">
        <Row>
          <Col xs={12} md={6} lg={3} className="mb-3">
            <LabelValue
              label={t("ORGANIZER")}
              value={
                entity.mission
                  ? getLocalizedText(entity.mission.name_ar, entity.mission.name_en) ||
                    entity.mission.name
                  : "-"
              }
              direction="vertical"
            />
          </Col>
          <Col xs={12} md={6} lg={3} className="mb-3">
            <LabelValue
              label={t("SUBORDINATE_ORGANIZER")}
              value={entity.subordinate_organizer || "---"}
              direction="vertical"
            />
          </Col>
          <Col xs={12} md={6} lg={3} className="mb-3">
            <LabelValue
              label={t("CAMPAIGN_OR_GROUP_NAME")}
              value={entity.name || "-"}
              direction="vertical"
            />
          </Col>
          <Col xs={12} md={6} lg={3} className="mb-3">
            <LabelValue
              label={t("SERVICE_CENTER")}
              value={
                entity.service_center
                  ? getLocalizedText(
                      entity.service_center.name_ar,
                      entity.service_center.name_en
                    ) || entity.service_center.name
                  : "-"
              }
              direction="vertical"
            />
          </Col>
        </Row>

        <Row>
          <Col xs={12} md={6} lg={3} className="mb-3">
            <LabelValue
              label={t("HOUSING_CONTRACTS_IN_MAKKAH")}
              value={formatContracts(makkahContracts)}
              direction="vertical"
            />
          </Col>
          <Col xs={12} md={6} lg={3} className="mb-3">
            <LabelValue
              label={t("HOUSING_CONTRACTS_IN_MADINAH")}
              value={formatContracts(madinahContracts)}
              direction="vertical"
            />
          </Col>
          <Col xs={6} md={3} className="mb-3">
            <LabelValue
              label={t("PILGRIMS_COUNT")}
              value={totalPilgrims}
              direction="vertical"
            />
          </Col>
          <Col xs={6} md={3} className="mb-3">
            <LabelValue
              label={t("CHECKED_IN_MAKKAH")}
              value={stats?.checkedInMakkah || "-"}
              direction="vertical"
            />
          </Col>
          <Col xs={6} md={3} className="mb-3">
            <LabelValue
              label={t("CHECKED_IN_MADINA")}
              value={stats?.checkedInMadina || "-"}
              direction="vertical"
            />
          </Col>
          <Col xs={6} md={3} className="mb-3">
            <LabelValue
              label={t("NUSUK_ACTIVATED_CARDS")}
              value={stats?.activatedCards}
              direction="vertical"
            />
          </Col>
          <Col xs={6} md={3} className="mb-3">
            <LabelValue
              label={t("NUSUK_DEACTIVATED_CARDS")}
              value={stats?.deactivatedCards}
              direction="vertical"
            />
          </Col>
        </Row>
      </div>
    </div>
  );
});

export const NusukCardsInquiry = () => {
  const { t } = useTranslation();
  const { getLocalizedText } = useLocalizedText();
  const { mutate: lookupPilgrims, isPending: isLookupPending } =
    useNusukCardsLookupPilgrimsMutation();
  const { cachedBasicData } = useCachedBasicData();

  const allContracts = useMemo(
    () => cachedBasicData?.contracts ?? [],
    [cachedBasicData?.contracts]
  );

  const [lookupType, setLookupType] = useState(LOOKUP_TYPES.campaign);
  const [lookupData, setLookupData] = useState(null);
  const [lookupRequest, setLookupRequest] = useState(null);

  const {
    control,
    formState: { errors },
    getValues,
    resetField,
  } = useForm();

  const isGroupLookup = lookupType === LOOKUP_TYPES.group;
  const entity = lookupData?.campaign || lookupData?.group || null;
  const totalPilgrims = lookupData?.pagination.total ?? 0;

  // ─── Contract filtering ────────────────────────────────────────────

  const selectedContracts = useMemo(
    () =>
      allContracts.filter((c) =>
        (entity?.contract_ids ?? []).some(
          (id) => String(id) === String(c.id)
        )
      ),
    [entity?.contract_ids, allContracts]
  );

  const makkahContracts = useMemo(
    () =>
      selectedContracts.filter(
        (c) => String(c.city_id) === String(NUSUK_CITY_ID.makkah)
      ),
    [selectedContracts]
  );

  const madinahContracts = useMemo(
    () =>
      selectedContracts.filter(
        (c) => String(c.city_id) === String(NUSUK_CITY_ID.madina)
      ),
    [selectedContracts]
  );

  // ─── Stats ─────────────────────────────────────────────────────────

  const stats = useMemo(() => {
    if (!lookupData) return null;
    const ent = lookupData.campaign || lookupData.group || {};
    return {
      totalPilgrims: lookupData.total_pilgrims ?? lookupData.pilgrims?.length ?? 0,
      checkedInMakkah: ent.checked_in_pilgrims_in_makkah ?? 0,
      checkedInMadina: ent.checked_in_pilgrims_in_madina ?? 0,
      activatedCards: lookupData.activated_cards ?? 0,
      deactivatedCards: lookupData.deactivated_cards ?? 0,
    };
  }, [lookupData]);

  // ─── Handlers ──────────────────────────────────────────────────────

  const handleLookup = useCallback(
    (type) => {
      const number = getValues("campaign_group_number");
      if (!number?.toString().trim()) {
        toast.warning(t("FIELD_REQUIRED"));
        return;
      }

      const trimmedNumber = number.toString().trim();

      lookupPilgrims(
        { type, number: trimmedNumber },
        {
          onSuccess: (res) => {
            const data = res?.data?.data || res?.data || {};
            setLookupData(data);
            setLookupRequest({ type, number: trimmedNumber });
          },
          onError: () => {
            setLookupData(null);
            setLookupRequest(null);
            toast.error(
              t("NO_RESULTS_FOUND", { defaultValue: t("NO_RESULTS") })
            );
          },
        }
      );
    },
    [getValues, lookupPilgrims, t]
  );

  const handleLookupTypeChange = useCallback((val) => {
    setLookupType(val);
    setLookupData(null);
    setLookupRequest(null);
    resetField("campaign_group_number");
  }, [resetField]);

  // ─── Render ────────────────────────────────────────────────────────

  return (
    <Stack gap={2}>
      <PageHeader title={t("NUSUK_INQUIRY_SCREEN")} sticky={false} />

      <Container fluid>
        <div className="card border rounded-3 mb-4 py-3 px-4">
          <Flex align="center" gap={12} className="mb-3">
            <Segmented
              value={lookupType}
              onChange={handleLookupTypeChange}
              options={[
                {
                  label: <span className="mb-0 d-inline-block">{t("CAMPAIGN_NUMBER")}</span>,
                  value: LOOKUP_TYPES.campaign,
                },
                {
                  label: <span className="mb-0 d-inline-block">{t("GROUP_NUMBER")}</span>,
                  value: LOOKUP_TYPES.group,
                },
              ]}
            />
          </Flex>

          <div className="d-flex align-items-end gap-3">
            <Stack gap={1} className="flex-grow-1">
              <label className="fw-bold required">
                {isGroupLookup ? t("GROUP_NUMBER") : t("CAMPAIGN_NUMBER")}
              </label>
              <RHFController
                name="campaign_group_number"
                control={control}
                rules={{ required: t("FIELD_REQUIRED") }}
                render={({ field }) => (
                  <>
                    <AntInput
                      {...field}
                      placeholder={isGroupLookup ? t("GROUP_NUMBER") : t("CAMPAIGN_NUMBER")}
                      status={errors.campaign_group_number ? "error" : undefined}
                      onPressEnter={(e) => {
                        e.preventDefault();
                        handleLookup(lookupType);
                      }}
                    />
                    {errors.campaign_group_number && (
                      <div className="text-danger mt-1">
                        {errors.campaign_group_number.message}
                      </div>
                    )}
                  </>
                )}
              />
            </Stack>
            <Button
              variant="outline-primary"
              type="button"
              disabled={isLookupPending}
              onClick={() => handleLookup(lookupType)}
              style={{ height: 50, minWidth: 50 }}
            >
              {isLookupPending ? (
                <Spinner size="sm" />
              ) : (
                <HugeiconsIcon icon={Search01FreeIcons} size={18} />
              )}
            </Button>
          </div>

          <EntityInfoCard
            entity={entity}
            stats={stats}
            makkahContracts={makkahContracts}
            madinahContracts={madinahContracts}
            t={t}
            getLocalizedText={getLocalizedText}
            totalPilgrims={totalPilgrims}
          />
        </div>

        {lookupRequest && (
          <NusukCardsInquiryTable
            lookupRequest={lookupRequest}
            allContracts={allContracts}
          />
        )}
      </Container>
    </Stack>
  );
};
